import barcodeGuide from "@assets/images/barcode_guide.svg";
import barcodeIcon from "@assets/images/barcode.svg";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import makeStyles from "@material-ui/core/styles/makeStyles";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import CardTitle from "@saleor/components/CardTitle";
import { OrderList_orders_edges_node } from "@saleor/motherOrders/types/OrderList";
import {
  DeliveryTypeEnum,
  SmopOrderStatusEnum
} from "@saleor/types/globalTypes";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import React, { Fragment, useEffect, useRef, useState } from "react";

import { OrderDetails_order } from "../../types/OrderDetails";

const useStyles = makeStyles(
  () => ({
    afterCheck: {
      "&:after": {
        backgroundColor: "#fff",
        content: "''",
        height: "3px",
        left: "7px",
        position: "absolute",
        top: "14px",
        width: "4px"
      }
    },
    avatar: {
      border: "1px solid #ccc",
      borderRadius: "5px",
      height: "40px",
      marginBottom: "10px",
      marginRight: "13px",
      overflow: "hidden",
      padding: "5px",
      width: "40px"
    },
    avatarImg: {
      height: "100%",
      maxHeight: "30px",
      maxWidth: "30px",
      objectFit: "cover",
      width: "100%"
    },
    barcodeContent: {
      "@media (max-width: 900px)": {
        padding: "0"
      },
      width: "800px"
    },
    barcodeGuide: {
      "@media (max-width: 768px)": {
        padding: "70px 60px"
      },
      left: "0",
      padding: "45px 60px",
      position: "absolute",
      right: "0",
      zIndex: 1
    },
    barcodeGuideImg: {
      width: "100%"
    },
    beforeCheck: {
      "&:before": {
        backgroundColor: "#fff",
        content: "''",
        height: "12px",
        left: "11px",
        position: "absolute",
        top: "5px",
        width: "3px"
      }
    },
    brandName: {
      marginTop: "-8px"
    },
    cameraButton: {
      alignItems: "center",
      cursor: "pointer",
      paddingLeft: "5px",
      position: "absolute",
      right: "12px",
      top: "6px",
      zIndex: 9
    },
    cameraButtonImg: {
      width: "25px"
    },
    cameraDialog: {
      "& .MuiDialog-paperWidthMd": {
        maxWidth: "850px"
      }
    },
    cameraDisabled: {
      cursor: "not-allowed",
      opacity: "0.5",
      pointerEvents: "none"
    },
    checkMark: {
      WebkitTransform: "rotate(45deg)",
      background: "#3AB74E",
      borderRadius: "50%",
      display: "inline-block",
      height: "22px",
      marginBottom: "5px",
      msTransform: "rotate(45deg)",
      transform: "rotate(45deg)",
      width: "22px"
    },
    contentTop: {
      display: "-webkit-box"
    },
    flexContent: {
      flexDirection: "column"
    },
    parcelCamera: {
      maxHeight: "300px"
    },
    parcelNo: {
      marginRight: "10px"
    },
    parcelNoContent: {
      display: "flex",
      width: "100%"
    },
    parcels: {
      marginTop: "10px"
    },
    requiredLabel: {
      "& span": {
        "&:before": {
          color: "red",
          content: "'*'",
          marginLeft: "-12px",
          position: "absolute"
        }
      }
    },
    searchForm: {
      "& input[type=text]": {
        paddingRight: "52px"
      },
      display: "flex",
      flex: 1,
      position: "relative"
    },
    textField: {
      padding: "0px !important"
    }
  }),
  {
    name: "OrderParcelID"
  }
);

export interface OrderParcelIDProps {
  onSubmitMarkAsValid: (storeOrderId: any, parcelNo: any) => void;
  onSubmitOpsScan?: (data: any) => void;
  onSubmitRunnerScan?: (data: any) => void;
  onSubmitParcelNo?: (data: any) => void;
  order: OrderDetails_order;
  userType?: string;
  disableAction: boolean;
}
interface ValidatedForPackingBy {
  __typename: string;
  email: string;
  firstName: string;
  id: string;
}

const OrderParcelID: React.FC<OrderParcelIDProps> = props => {
  const { order, onSubmitMarkAsValid, disableAction } = props;
  const classes = useStyles(props);

  const [parcelValues, setParcelValues] = React.useState([]);
  const [scannerReaderId, setScannerReaderId] = useState(null);
  const [barcodeScanner, setBarcodeScanner] = useState<Html5Qrcode | null>(
    null
  );
  const scannerRef = useRef<Html5Qrcode | null>(null);

  const inputProps = {
    placeholder: "ParcelID",
    style: { padding: "8px" }
  };

  const onEnterPress = (keypress, subOrderId) => {
    if (keypress.key === "Enter") {
      const storeOrderId = parcelValues.find(parcelValue => {
        if (parcelValue.id === subOrderId) {
          return parcelValue.id;
        }
      });

      const temp_lines = [...parcelValues];
      const index = temp_lines.findIndex(i => i.id === subOrderId);
      temp_lines[index] = { ...temp_lines[index] };
      temp_lines[index].parcelNo = "";
      setParcelValues(temp_lines);

      onSubmitMarkAsValid(storeOrderId.id, keypress.target.value);
    }
  };

  // checkDisable() checks if a parcel number exists, the status is "RECEIVED_AT_FC" and has been validated for packing

  const checkDisable = (
    order: OrderList_orders_edges_node,
    parcelNo: string,
    validatedForPackingBy: ValidatedForPackingBy
  ) => {
    if (
      !parcelNo ||
      (order && order.smopStatus !== SmopOrderStatusEnum.RECEIVED_AT_FC)
    ) {
      return true;
    }

    if (validatedForPackingBy) {
      return true;
    } else {
      return false;
    }
  };

  const [open, setOpen] = useState(false);

  useEffect(() => {
    const initializeScanner = () => {
      if (
        navigator.mediaDevices &&
        typeof navigator.mediaDevices.getUserMedia === "function"
      ) {
        setTimeout(() => {
          const readerElementId = "parcelCamera";

          const readerElement = document.querySelector("#parcelCamera");

          if (readerElement) {
            const scanner = new Html5Qrcode(readerElementId);

            const detectorConfig = {
              formatsToSupport: [
                Html5QrcodeSupportedFormats.CODE_128,
                Html5QrcodeSupportedFormats.EAN_13,
                Html5QrcodeSupportedFormats.EAN_8
              ],
              fps: 10,
              videoConstraints: {
                height: 300,
                width: 800
              }
            };

            const cameraConfig = {
              facingMode: "environment"
            };

            const onScanSuccess = (decodedText: string) => {
              const temp_object = {
                id: scannerReaderId,
                parcelNo: decodedText
              };

              const checkIfExist = () =>
                parcelValues.some(({ id }) => id === scannerReaderId);

              if (parcelValues.length === 0) {
                setParcelValues([temp_object]);
              } else {
                if (checkIfExist()) {
                  const temp_lines = [...parcelValues];
                  const index = temp_lines.findIndex(
                    i => i.id === scannerReaderId
                  );
                  temp_lines[index] = { ...temp_lines[index] };
                  temp_lines[index].parcelNo = decodedText;
                  setParcelValues(temp_lines);
                } else {
                  setParcelValues([...parcelValues, temp_object]);
                }
              }

              setBarcodeScanner(null);
              setOpen(false);
            };

            const onScanError = (error: any) => error;

            scanner
              .start(cameraConfig, detectorConfig, onScanSuccess, onScanError)
              .then(() => {
                setBarcodeScanner(scanner);
                scannerRef.current = scanner;
              })
              .catch(onScanError);
          }
        }, 1000);
      }
    };

    if (open && !barcodeScanner && !scannerRef.current) {
      initializeScanner();
    }

    return () => {
      if (scannerRef.current && scannerRef.current.isScanning) {
        scannerRef.current
          .stop()
          .then(() => {
            if (scannerRef.current) {
              scannerRef.current.clear();
            }
          })
          .catch(error => error);

        setBarcodeScanner(null);
        scannerRef.current = null;
      }
    };
  }, [open, barcodeScanner, parcelValues]);

  const handleClickOpen = id => {
    setOpen(true);
    setScannerReaderId(id);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const onChangeQuantity = (parcelNo, subOrderid) => {
    const temp_object = {
      id: subOrderid,
      parcelNo
    };

    const checkIfExist = () => parcelValues.some(({ id }) => id === subOrderid);

    if (parcelValues.length === 0) {
      setParcelValues([temp_object]);
    } else {
      if (checkIfExist()) {
        const temp_lines = [...parcelValues];
        const index = temp_lines.findIndex(i => i.id === subOrderid);
        temp_lines[index] = { ...temp_lines[index] };
        temp_lines[index].parcelNo = parcelNo;
        setParcelValues(temp_lines);
      } else {
        setParcelValues([...parcelValues, temp_object]);
      }
    }
  };

  const getTextFieldValue = subOrderid =>
    parcelValues
      .filter(function(obj) {
        return obj.id === subOrderid;
      })
      .map(function(obj) {
        return obj.parcelNo;
      });

  return (
    <Card>
      <CardTitle
        customId="txtAdminFulfillmentMotherOrderDetailsPageParcelId"
        title="Parcel ID"
        className={
          order?.shippingMethod?.deliveryType === DeliveryTypeEnum.NEXT_DAY ||
          order?.shippingMethod?.deliveryType ===
            DeliveryTypeEnum.NATIONWIDE_DELIVERY
            ? classes.requiredLabel
            : null
        }
      />
      <CardContent
        id="cmpAdminFulfillmentMotherOrderDetailsPageCardContent"
        style={{ paddingTop: "0px" }}
      >
        {order &&
          order.subOrders &&
          order.subOrders.map((subOrder, index) => (
            <div key={index} className={classes.parcels}>
              {subOrder.parcelNo ? (
                <div className={classes.contentTop}>
                  <div className={classes.avatar}>
                    <img
                      className={classes.avatarImg}
                      src={subOrder?.tenant?.brand?.logo?.url}
                    />
                  </div>
                  <div>
                    <div className={classes.parcelNoContent}>
                      <Typography
                        variant="subtitle1"
                        className={classes.parcelNo}
                      >
                        {subOrder.parcelNo}
                      </Typography>
                      {subOrder.validatedForPackingBy ? (
                        <span
                          className={`${classes.checkMark} ${classes.beforeCheck} ${classes.afterCheck}`}
                        ></span>
                      ) : null}
                    </div>
                    <div className={classes.brandName}>
                      {subOrder.tenant.brand.name}
                    </div>
                  </div>
                </div>
              ) : (
                <Typography
                  id="txtAdminFulfillmentMotherOrderDetailsPageNoParcelId"
                  variant="subtitle1"
                >
                  No parcel ID to be shown
                </Typography>
              )}
              <div className={classes.searchForm}>
                <TextField
                  id="txtAdminFulfillmentMotherOrderDetailsPageParcelId"
                  fullWidth
                  disabled={
                    checkDisable(
                      order,
                      subOrder.parcelNo,
                      subOrder.validatedForPackingBy
                    ) || disableAction
                  }
                  className={classes.textField}
                  inputProps={inputProps}
                  defaultValue=""
                  value={getTextFieldValue(subOrder.id)}
                  onChange={e => onChangeQuantity(e.target.value, subOrder.id)}
                  onKeyPress={keypress => onEnterPress(keypress, subOrder.id)}
                  InputProps={{
                    placeholder: "Parcel ID"
                  }}
                />
                <div
                  className={
                    classes.cameraButton +
                    (checkDisable(
                      order,
                      subOrder.parcelNo,
                      subOrder.validatedForPackingBy
                    )
                      ? " " + classes.cameraDisabled
                      : "")
                  }
                  onClick={() =>
                    checkDisable(
                      order,
                      subOrder.parcelNo,
                      subOrder.validatedForPackingBy
                    )
                      ? undefined
                      : handleClickOpen(subOrder.id)
                  }
                >
                  <img
                    src={barcodeIcon}
                    alt="Barcode Icon"
                    className={classes.cameraButtonImg}
                  />
                </div>
              </div>
            </div>
          ))}
      </CardContent>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="md"
        className={classes.cameraDialog}
      >
        <DialogTitle id="alert-dialog-title">Scan Parcel ID</DialogTitle>
        <DialogContent className={classes.barcodeContent}>
          <Fragment>
            <div className={classes.barcodeGuide}>
              <img className={classes.barcodeGuideImg} src={barcodeGuide} />
            </div>
            <div
              id="parcelCamera"
              className={classes.parcelCamera}
              style={{ width: "100%" }}
            ></div>
          </Fragment>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

OrderParcelID.displayName = "ParcelID";
export default OrderParcelID;
